import { useState, useEffect } from 'react';
import { colors } from '../constants/colors';
import { getStripeConnectStatus, startStripeOnboarding } from '../api/client';
import type { StripeConnectStatus } from '../types';
import { CheckCircle, AlertCircle, Loader2 } from 'lucide-react';

interface StripeOnboardingSectionProps {
  title?: string;
}

export function StripeOnboardingSection({ title = 'Payouts' }: StripeOnboardingSectionProps) {
  const [status, setStatus] = useState<StripeConnectStatus | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isActionLoading, setIsActionLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadStatus();
  }, []);

  const loadStatus = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await getStripeConnectStatus();
      setStatus(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load status');
    } finally {
      setIsLoading(false);
    }
  };

  const handleStartOnboarding = async () => {
    try {
      setIsActionLoading(true);
      setError(null);
      const { onboardingUrl } = await startStripeOnboarding();
      window.location.href = onboardingUrl;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start onboarding');
      setIsActionLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div style={styles.section}>
        <h2 style={styles.title}>{title}</h2>
        <div style={styles.loading}>Loading status...</div>
      </div>
    );
  }

  const getStatusBadge = () => {
    if (!status) return null;

    if (status.status === 'COMPLETE' && status.chargesEnabled) {
      return (
        <div style={{ ...styles.statusBadge, ...styles.statusComplete }}>
          <CheckCircle size={16} />
          Active
        </div>
      );
    }

    if (status.status === 'IN_PROGRESS') {
      return (
        <div style={{ ...styles.statusBadge, ...styles.statusPending }}>
          <AlertCircle size={16} />
          Incomplete
        </div>
      );
    }

    return (
      <div style={{ ...styles.statusBadge, ...styles.statusNotStarted }}>
        <AlertCircle size={16} />
        Not Set Up
      </div>
    );
  };

  const renderContent = () => {
    if (!status || status.status === 'NOT_STARTED') {
      return (
        <>
          <p style={styles.description}>
            Connect your bank account to receive payouts from your sales.
            Stripe handles all payment processing securely.
          </p>
          <button
            style={styles.primaryButton}
            onClick={handleStartOnboarding}
            disabled={isActionLoading}
          >
            {isActionLoading ? (
              <>
                <Loader2 size={18} style={{ animation: 'spin 1s linear infinite' }} />
                Loading...
              </>
            ) : (
              'Set Up Payouts'
            )}
          </button>
        </>
      );
    }

    if (status.status === 'IN_PROGRESS' || !status.chargesEnabled) {
      return (
        <>
          <p style={styles.description}>
            Your payout setup is incomplete. Please finish setting up your account to start receiving payments.
          </p>
          <div style={styles.statusDetails}>
            <div style={styles.statusItem}>
              <span style={styles.statusLabel}>Details Submitted</span>
              <span style={status.detailsSubmitted ? styles.statusYes : styles.statusNo}>
                {status.detailsSubmitted ? 'Yes' : 'No'}
              </span>
            </div>
            <div style={styles.statusItem}>
              <span style={styles.statusLabel}>Accept Payments</span>
              <span style={status.chargesEnabled ? styles.statusYes : styles.statusNo}>
                {status.chargesEnabled ? 'Ready' : 'Not Ready'}
              </span>
            </div>
            <div style={styles.statusItem}>
              <span style={styles.statusLabel}>Receive Payouts</span>
              <span style={status.payoutsEnabled ? styles.statusYes : styles.statusNo}>
                {status.payoutsEnabled ? 'Ready' : 'Not Ready'}
              </span>
            </div>
          </div>
          <button
            style={styles.primaryButton}
            onClick={handleStartOnboarding}
            disabled={isActionLoading}
          >
            {isActionLoading ? (
              <>
                <Loader2 size={18} style={{ animation: 'spin 1s linear infinite' }} />
                Loading...
              </>
            ) : (
              'Continue Setup'
            )}
          </button>
        </>
      );
    }

    // Complete status
    return (
      <>
        <p style={styles.description}>
          Your payout account is set up and ready to receive payments from your sales.
        </p>
        <div style={styles.statusDetails}>
          {status.bankName && status.bankLast4 ? (
            <div style={styles.statusItem}>
              <span style={styles.statusLabel}>Payout Method</span>
              <span style={styles.statusYes}>{status.bankName} ****{status.bankLast4}</span>
            </div>
          ) : (
            <>
              <div style={styles.statusItem}>
                <span style={styles.statusLabel}>Accept Payments</span>
                <span style={styles.statusYes}>Ready</span>
              </div>
              <div style={styles.statusItem}>
                <span style={styles.statusLabel}>Receive Payouts</span>
                <span style={status.payoutsEnabled ? styles.statusYes : styles.statusNo}>
                  {status.payoutsEnabled ? 'Ready' : 'Pending'}
                </span>
              </div>
            </>
          )}
        </div>
      </>
    );
  };

  return (
    <div style={styles.section}>
      <div style={styles.header}>
        <h2 style={styles.title}>{title}</h2>
        {getStatusBadge()}
      </div>

      {error && <div style={styles.error}>{error}</div>}

      {renderContent()}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  section: {
    padding: '20px',
    backgroundColor: colors.white,
    borderRadius: '12px',
    border: `1px solid ${colors.gray[300]}`,
  },
  loading: {
    textAlign: 'center',
    padding: '20px',
    color: colors.gray[600],
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '16px',
  },
  title: {
    margin: 0,
    fontSize: '18px',
    fontWeight: 700,
    color: colors.ink.black,
  },
  statusBadge: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '6px 12px',
    borderRadius: '100px',
    fontSize: '13px',
    fontWeight: 600,
  },
  statusComplete: {
    backgroundColor: '#D1FAE5',
    color: '#059669',
  },
  statusPending: {
    backgroundColor: '#FEF3C7',
    color: '#D97706',
  },
  statusNotStarted: {
    backgroundColor: colors.gray[100],
    color: colors.gray[600],
  },
  description: {
    margin: '0 0 16px 0',
    fontSize: '14px',
    color: colors.gray[600],
    lineHeight: 1.5,
  },
  statusDetails: {
    padding: '12px',
    backgroundColor: colors.gray[100],
    borderRadius: '8px',
    marginBottom: '16px',
  },
  statusItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '8px 0',
  },
  statusLabel: {
    fontSize: '14px',
    color: colors.gray[600],
  },
  statusYes: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#059669',
  },
  statusNo: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#DC2626',
  },
  error: {
    padding: '12px',
    marginBottom: '16px',
    backgroundColor: '#FEE2E2',
    color: '#DC2626',
    borderRadius: '8px',
    fontSize: '14px',
  },
  primaryButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    width: '100%',
    padding: '14px',
    fontSize: '15px',
    fontWeight: 600,
    color: colors.white,
    backgroundColor: colors.brand.blue,
    border: 'none',
    borderRadius: '10px',
    cursor: 'pointer',
  },
};
